import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X } from "lucide-react";
import api, { formatError } from "@/lib/api";

// Admin-only exceptional action for a PAID home-delivery charge.
export default function ChargeAdjustmentDialog({ detail, onClose, onApplied }) {
  const [kind, setKind] = useState("waiver");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const paid = Math.round((detail.payment?.amount || 0) / 100);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/submissions/${detail.id}/charge-adjustment`, { kind, reason: reason.trim(), confirm });
      const patch = kind === "waiver"
        ? { home_delivery_chargeable: false, home_delivery_amount_paise: 0, charge_waived: true }
        : { payment: { ...detail.payment, refund: { amount: detail.payment?.amount, at: data.adjustment.at, by: data.adjustment.by, reason: data.adjustment.reason } } };
      onApplied({ ...patch, charge_adjustments: [...(detail.charge_adjustments || []), data.adjustment] });
      toast.success(kind === "waiver" ? `Waiver recorded for ${detail.invoice_number}` : `Refund recorded for ${detail.invoice_number}`);
      onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink/40 p-4" data-testid="charge-adjustment-dialog" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-medium text-ink inline-flex items-center gap-2"><AlertTriangle size={15} className="text-maroon" /> Exceptional charge adjustment · {detail.invoice_number}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted2 hover:text-maroon"><X size={16} /></button>
        </div>
        <p className="mt-2 text-xs text-muted2">Original payment of <span className="font-semibold text-ink">₹{paid}</span> stays on record. This action is logged with your account and timestamp.</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[["waiver", "Waiver", "Charge no longer applies; payment record is kept."], ["refund", "Refund", "Record that ₹" + paid + " was refunded to the customer."]].map(([v, l, d]) => (
            <button key={v} type="button" data-testid={`charge-adjust-${v}`} onClick={() => setKind(v)}
              className={`rounded-xl border p-3 text-left transition-colors ${kind === v ? "border-maroon bg-maroon/[0.06]" : "border-line hover:border-maroon/40"}`}>
              <p className="text-sm font-medium text-ink">{l}</p><p className="text-[11px] text-muted2 mt-0.5">{d}</p>
            </button>
          ))}
        </div>
        <textarea data-testid="charge-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
          placeholder="Reason (required, at least 5 characters)" className="mt-3 w-full rounded-xl border border-line px-3 py-2 text-sm focus:outline-none focus:border-maroon" />
        <label className="mt-3 flex items-start gap-2 text-xs text-ink">
          <input type="checkbox" data-testid="charge-adjust-confirm" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5" />
          I confirm this {kind} for invoice {detail.invoice_number}. {kind === "refund" ? "The refund itself must be issued in Razorpay; this only records it." : "This does not refund the customer."}
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-outline-maroon !py-2 !px-4 !text-xs">Cancel</button>
          <button type="button" data-testid="charge-adjust-submit" onClick={submit} disabled={busy || !confirm || reason.trim().length < 5}
            className="rounded-full bg-maroon px-4 py-2 text-xs font-medium text-ivory hover:bg-maroon-hover disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy && <Loader2 size={13} className="animate-spin" />} Record {kind}
          </button>
        </div>
      </div>
    </div>
  );
}
